import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Dialog from "../../../components/Dialog";
import Autocomplete2 from "../../../components/Autocomplete2";
import { notify } from "../../../reducers/NotifierReducer";
import formStyles, { Row, gridStyle } from "../../../utils/formStyles";
import { findSecComponents, createSecComponent } from "../SecFunction";
import { handleError } from "../../../reducers/ErrorReducer";
import Markdown from "../../../components/Markdown";
import { permissionLabelOptions } from "../../../utils/constants";

const useStyles = makeStyles((theme) => ({
  ...formStyles,
  header: {
    marginLeft: 8,
  },
  pageWrap: {
    width: 600,
    minHeight: 300,
  },
  item: gridStyle("200", "100%"),
}));

const options = [
  { Code: "Modules", Type: "Modules", tabValue: "modules" },
  { Code: "Security", Type: "Security", tabValue: "security" },
  { Code: "Codes", Type: "Codes", tabValue: "codes" },
  {
    Code: "Applicants",
    Type: "Applicants",
    tabValue: "applicants",
  },
  {
    Code: "Employees",
    Type: "Employees",
    tabValue: "employees",
  },
  {
    Code: "Training",
    Type: "Training",
    tabValue: "training",
  },
];

function ExampleWindow(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [option, setOption] = useState(null);
  const [components, setComponents] = useState([]);
  const [labels, setLabels] = useState([]);
  const [label, setLabel] = useState("");
  const [labelError, setLabelError] = useState(null);

  useEffect(() => {
    getSecComponents();
    const option = options.find((opt) => opt.tabValue === props.tabValue);
    if (!option) {
      return dispatch(notify("Permissions configuration error", "error"));
    }
    setOption(option);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!components.length || !option) return;
    const labels = components
      .filter((c) => c.Type === option.Type)
      .map((c) => c.Label.toLowerCase());
    setLabels(labels);
    setLabel("");
    // eslint-disable-next-line
  }, [option, components]);

  const getSecComponents = async () => {
    try {
      const components = await findSecComponents();
      setComponents(components);
    } catch (err) {
      dispatch(handleError(err));
    }
  };

  const close = () => {
    props.close();
  };

  const create = async () => {
    const data = {
      Label: label,
      Type: option.Type,
    };
    try {
      await createSecComponent(data);
      dispatch(notify("New Component Created", "success"));
      getSecComponents();
      props.fetchData();
    } catch (err) {
      dispatch(handleError(err));
    }
  };

  const getSQL = () => {
    if (!option || !label) return "```bash\nComplete the form first\n\n```";
    return (
      "```sql\n-- insert_secComponentAMS_" +
      `${option.Type}_${label.replace(
        /\s+/g,
        "_"
      )}.sql\n\nINSERT INTO secComponentAMS (Label, Type)\n` +
      `VALUES('${label}', '${option.Type}')` +
      "\n```"
    );
  };

  const renderLabel = () => {
    const optionsForTab = option
      ? (permissionLabelOptions[option.tabValue] || []).map((val) => ({
          label: val,
        }))
      : [];

    return (
      <Autocomplete2
        className={classes.item}
        options={optionsForTab}
        value={label ? { label } : null}
        getOptionLabel={(opt) =>
          typeof opt === "string" ? opt : opt?.label || ""
        }
        renderOption={(props, option) => (
          <li {...props}>
            {typeof option === "string" ? option : option.label}
          </li>
        )}
        onChange={(event, val) => {
          const newLabel = typeof val === "string" ? val : val?.label || "";
          setLabel(newLabel);

          if (newLabel && labels.includes(newLabel.toLowerCase())) {
            setLabelError("The value is already in use");
          } else {
            setLabelError(null);
          }
        }}
        onInputChange={(event, val, reason) => {
          if (reason === "input") setLabel(val);
        }}
        error={labelError}
      />
    );
  };

  const renderActions = () => {
    return (
      <>
        <Button
          color="primary"
          variant="contained"
          onClick={create}
          disabled={!label || labelError !== null || !option}
        >
          <SaveIcon /> Create Permission
        </Button>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  return (
    <Dialog
      toolbar
      onClose={close}
      title="Add Permission"
      actions={renderActions()}
      maxWidth="xl"
    >
      <div className={classes.pageWrap}>
        <Row>
          <Autocomplete2
            className={classes.item}
            options={options}
            value={option}
            onChange={(ev, val) => setOption(val)}
            compact
          />
          {renderLabel()}
        </Row>
        <div>Generated SQL:</div>
        <Markdown code={getSQL()} />
      </div>
    </Dialog>
  );
}

export default ExampleWindow;
